import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Lock } from 'lucide-react';
import { formatHours } from '../../lib/format';
import { cx } from '../../lib/cx';
import { Modal, useToast } from '../../components';
import { SprintAdmin } from '../sprints/SprintAdmin';
import { LOAD_TEXT, Meter, StateChip } from '../sprints/parts';
import { TIMELINE_STATE_LABEL, isOverdueToStart, sprintDate, sprintRange } from '../sprints/sprintMeta';
import { invalidatePlanning, lockSprint, startSprint } from '../sprints/api';
import { workingDayOf } from '@rundown/domain';
import { Column, PlanCard } from './parts';
import type { PlanColumn as PlanColumnModel } from './usePlanBoard';

/**
 * One sprint, as a column you can drop work into and act on.
 *
 * The header carries the sprint's whole lifecycle — state, dates, load, and
 * the single action available next. That is the merge: the Sprints screen
 * owned lock/release and the Planning screen owned the columns, so doing
 * either meant knowing which screen it lived on.
 */
export function PlanColumn({
  workspaceId,
  column,
  today,
  holidays,
  holidaysWithin,
  canCommit,
  canAdmin,
  draggable,
  pending,
  anyRunning,
  selected,
  onSelect,
}: {
  readonly workspaceId: string;
  readonly column: PlanColumnModel;
  readonly today: string;
  /** Dates only, for the working-day count. */
  readonly holidays: readonly string[];
  /** The same days, named, for the warning chip. */
  readonly holidaysWithin: (
    startsOn: string,
    endsOn: string,
  ) => readonly { onDate: string; name: string; estimated: boolean }[];
  readonly canCommit: boolean;
  readonly canAdmin: boolean;
  readonly draggable: boolean;
  readonly pending: ReadonlyMap<string, string>;
  /** Some sprint is already running, so nothing else may start. */
  readonly anyRunning: boolean;
  readonly selected: boolean;
  readonly onSelect: () => void;
}) {
  const [lockOpen, setLockOpen] = useState(false);
  const queryClient = useQueryClient();
  const { say, warn } = useToast();
  const { sprint, state, items, hours } = column;

  const start = useMutation({
    mutationFn: () => startSprint(workspaceId, sprint.id),
    onSuccess: () => say(`${sprint.name} is running. It is now the week on Delivery.`),
    onError: (error) => warn(error.message),
    onSettled: () => invalidatePlanning(queryClient, workspaceId),
  });

  const day = state === 'started' ? workingDayOf(sprint.startsOn, sprint.endsOn, today, holidays) : null;
  const lost = holidaysWithin(sprint.startsOn, sprint.endsOn);
  const overdue = isOverdueToStart(sprint, today);

  return (
    <Column
      id={sprint.id}
      droppable={!column.frozen}
      className={cx(
        'w-[13.125rem] min-w-[13.125rem] flex-[1_0_13.125rem]',
        state === 'started' ? 'bg-success-tint' : 'bg-surface-2',
        selected && 'outline-2 outline-ink',
      )}
      header={
        <>
          <div className="flex items-start justify-between gap-1.5">
            {/* Two lines before it clamps, not one: the column is 210px and a
                real sprint name ("Sprint 12 — Address book migration") is
                wider than that, so a single truncated line would cut most
                names off at the number they all share. */}
            <button
              type="button"
              onClick={onSelect}
              className="line-clamp-2 min-w-0 text-left font-bold hover:text-accent"
              title={sprint.name}
            >
              {sprint.name}
            </button>
            {canAdmin && (
              <SprintAdmin workspaceId={workspaceId} sprint={sprint} state={sprint.state} trigger="icon" />
            )}
          </div>

          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            <StateChip state={state}>{TIMELINE_STATE_LABEL[state]}</StateChip>
            {column.frozen && <Lock aria-hidden size={11} className="text-muted" />}
            {day !== null && (
              <span className="fs-10.5 font-semibold text-muted numeric">
                day {day.day} of {day.of}
              </span>
            )}
          </div>

          <div className="mt-1 fs-11 text-muted">{sprintRange(sprint.startsOn, sprint.endsOn)}</div>

          {/* The holiday chip is the whole point of knowing about holidays: it
              has to be visible BEFORE the scope is locked, not discovered in
              the week the team is short a day. */}
          {lost.length > 0 && (
            <p className="mt-1 fs-10.5 leading-snug text-warn">
              ⚠{' '}
              {lost
                .map(
                  (holiday) =>
                    `${holiday.name} ${sprintDate(holiday.onDate)}${holiday.estimated ? ' (estimated)' : ''}`,
                )
                .join(' · ')}
            </p>
          )}

          <div className="mt-2">
            <Meter width={column.width} band={column.band} />
          </div>
          <div className="mt-1.5 fs-11 text-muted numeric">
            {formatHours(hours)} of {formatHours(sprint.plannableHours)}{' '}
            <span className={cx('font-bold', column.band !== null && LOAD_TEXT[column.band])}>
              · {column.loadLabel}
            </span>
          </div>

          {canCommit && <Lifecycle />}
        </>
      }
    >
      {items.length === 0 && (
        <p className="px-1 fs-11 text-faint">
          {draggable && !column.frozen ? 'Drop work here.' : 'Nothing lands here yet.'}
        </p>
      )}
      {items.map((card) => (
        <PlanCard
          key={`${card.mark}:${card.requestId}`}
          card={card}
          draggable={draggable && !column.frozen}
          pending={pending.has(card.requestId)}
        />
      ))}

      {lockOpen && (
        <LockSprintModal workspaceId={workspaceId} column={column} onClose={() => setLockOpen(false)} />
      )}
    </Column>
  );

  /**
   * One button, never two. A column offers only the single next step its state
   * allows, which is what stops "start the third sprint first" being a thing
   * the screen invites.
   */
  function Lifecycle() {
    if (sprint.state === 'planning') {
      if (items.length === 0) return null;
      return (
        <>
          <button
            type="button"
            onClick={() => setLockOpen(true)}
            className="mt-2.5 w-full rounded-control bg-ink px-2.5 py-1.5 fs-11.5 font-bold whitespace-nowrap text-white hover:bg-ink-soft"
          >
            Lock scope
          </button>
          <p className="mt-1.5 fs-10.5 leading-snug text-muted">
            Fixes what is in it and tells the client. Starting the week is separate.
          </p>
        </>
      );
    }

    if (sprint.state === 'locked') {
      return (
        <>
          <button
            type="button"
            onClick={() => start.mutate()}
            disabled={start.isPending || anyRunning}
            className="mt-2.5 w-full rounded-control bg-success px-2.5 py-1.5 fs-11.5 font-bold whitespace-nowrap text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {start.isPending ? 'Starting…' : 'Start sprint'}
          </button>
          <p className="mt-1.5 fs-10.5 leading-snug text-muted">
            {anyRunning
              ? 'Another sprint is running. Release it first.'
              : overdue
                ? `Due to have started ${sprintDate(sprint.startsOn, true)}.`
                : `Starts ${sprintDate(sprint.startsOn, true)}.`}
          </p>
        </>
      );
    }

    if (sprint.state === 'started') {
      return (
        <button
          type="button"
          onClick={onSelect}
          className="mt-2.5 w-full rounded-control border border-line-stronger bg-base px-2.5 py-1.5 fs-11.5 font-bold whitespace-nowrap text-ink hover:border-line-heavy"
        >
          Release sprint
        </button>
      );
    }

    return null;
  }
}

/**
 * What locking does, said before it happens.
 *
 * v0.3 §6.4 rule 1 — the client is notified and the scope is fixed — so the
 * person pressing it sees exactly the items being committed, including the
 * projected ones and the client's suggestions, and whether the week is already
 * over capacity.
 */
function LockSprintModal({
  workspaceId,
  column,
  onClose,
}: {
  readonly workspaceId: string;
  readonly column: PlanColumnModel;
  readonly onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const { say, warn } = useToast();
  const { sprint, items, hours } = column;
  const committing = items.filter((card) => card.mark !== 'removed');

  const lock = useMutation({
    mutationFn: () =>
      lockSprint(
        workspaceId,
        sprint.id,
        committing.map((card) => card.requestId),
      ),
    onSuccess: () => {
      say(
        `${sprint.name} is locked with ${committing.length} item${committing.length === 1 ? '' : 's'} (${formatHours(hours)}). The client has been notified.`,
      );
      onClose();
    },
    onError: (error) => warn(error.message),
    onSettled: () => invalidatePlanning(queryClient, workspaceId),
  });

  const counts = {
    committed: committing.filter((card) => card.mark === 'committed').length,
    placed: committing.filter((card) => card.mark === 'placed').length,
    projected: committing.filter((card) => card.mark === 'projected').length,
  };
  const over = column.band === 'overloaded';
  const unowned = committing.filter((card) => card.ownerId === null).length;

  return (
    <Modal
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={`Lock ${sprint.name}`}
      description="Fixes what is in this sprint and notifies the client. Starting the week is a separate step."
      footer={
        <>
          <button type="button" onClick={onClose} className="rounded-control px-3 py-1.5 fs-12.5 text-muted">
            Cancel
          </button>
          <button
            type="button"
            onClick={() => lock.mutate()}
            disabled={lock.isPending || committing.length === 0}
            className="rounded-control bg-ink px-3 py-1.5 fs-12.5 font-bold text-white disabled:opacity-50"
          >
            {lock.isPending ? 'Locking…' : 'Lock scope'}
          </button>
        </>
      }
    >
      <p className="fs-13 text-muted">From then on its scope moves only through an agreed change of plan.</p>

      <ul className="mt-3 flex flex-col gap-1 fs-12.5 text-ink">
        <li>
          <strong className="numeric">{committing.length}</strong> item
          {committing.length === 1 ? '' : 's'}, ≈{formatHours(hours)} of {formatHours(sprint.plannableHours)}{' '}
          plannable
        </li>
        {counts.placed > 0 && (
          <li className="text-muted">
            {counts.placed} the client suggested — locking accepts {counts.placed === 1 ? 'it' : 'them'}
          </li>
        )}
        {counts.projected > 0 && (
          <li className="text-muted">
            {counts.projected} pulled in by the ranking rather than placed by hand
          </li>
        )}
        {unowned > 0 && <li className="text-muted">{unowned} with nobody on it yet</li>}
      </ul>

      {over && (
        <p className="mt-3 rounded-control bg-danger-tint px-3 py-2 fs-12 text-danger">
          This is over capacity. Locking it promises a week the team has not got.
        </p>
      )}
    </Modal>
  );
}
